import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { Modal, InputText, Button, Toast } from '@insurancemenu/imenu-components-lib';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';

import FormsSpinner from 'components/common/loaders/FormSpinner';
import MainContactInformation from 'components/common/group/content/MainContactInformation';
import PhysicalAddressInformation from 'components/common/group/content/PhysicalAddressInformation';
import { useRootStore } from 'store/rootStore';
import { createStyles } from './styles';

interface GroupInfoEditModalProps {
  show: boolean;
  onClose: () => void;
}

const GroupInfoEditModal: React.FC<GroupInfoEditModalProps> = observer(({ show, onClose }) => {
  const rootStore = useRootStore();
  const {
    groupInfoModalStore: { groupForm, loading, saveGroupOnServer, requestZipCodeInfo },
    geoStore,
  } = rootStore;

  useEffect(() => {
    if (!groupForm.fields.county.value && groupForm.fields.zip.value.length === 5) {
      void requestZipCodeInfo();
    }
  }, [groupForm.fields.county.value]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (groupForm.isFormValid()) {
      await saveGroupOnServer();
      onClose();
    }
  };

  const onToastClose = (): void => {
    geoStore.setWrongZipCodeStatus(false);
  };

  const classes = createStyles();
  return (
    <Modal show={show} onClose={onClose} closable={false} width="71.2rem" className={classes.modal} id="group-edit">
      <div className={classes.body}>
        <h3 className={classes.title}>Group Information</h3>
        <form className={classes.form} onSubmit={onSubmit} id="group-edit-form">
          <div className={classes.formContent}>
            <InputText
              label="Group Name"
              name="name"
              value={groupForm.fields.name.value}
              onChange={groupForm.onFieldChange}
              className={classes.formInput}
              validationPassed={!groupForm.fields.name.unpassedValidationMsg}
              validationMessage={groupForm.fields.name.unpassedValidationMsg || ''}
            />
            <>
              <MainContactInformation
                firstName={groupForm.fields.firstName}
                lastName={groupForm.fields.lastName}
                primaryEmail={groupForm.fields.primaryEmail}
                title={groupForm.fields.title}
                primaryPhone={groupForm.fields.primaryPhone}
                onChange={groupForm.onFieldChange}
              />
              <PhysicalAddressInformation
                city={groupForm.fields.city}
                street={groupForm.fields.street}
                street2={groupForm.fields.street2}
                zip={groupForm.fields.zip}
                state={groupForm.fields.state}
                county={groupForm.fields.county}
                countiesOptions={geoStore.countiesAsSelectOptions}
                onChange={groupForm.onFieldChange}
              />
            </>
          </div>

          <footer className={classes.footer}>
            <div className={classes.actionsBlock}>
              <Button type="outlined" onClick={onClose} size="large">
                Cancel
              </Button>
              <Button
                id="save-group-button"
                color="primary"
                htmlType="submit"
                size="large"
                onClick={onSubmit}
                disabled={!groupForm.isFormValid()}
              >
                Save
              </Button>
            </div>
          </footer>
          <FormsSpinner display={loading} />
        </form>
      </div>
      <Toast
        show={geoStore.wrongZipCodeKeyedIn}
        onClose={onToastClose}
        colorType={ToastColorTypes.WARNING}
        className={classes.toastMessage}
      >
        Zip code is not valid!
      </Toast>
    </Modal>
  );
});

export default GroupInfoEditModal;
